import { ref } from "vue";
import { captureException } from "@sentry/vue";
import { adminV1OrganizationsAssociatedContactsRetrieve } from "@/api/admin-associated-contacts/admin-associated-contacts";
import { adminV1OrganizationsPaymentsBillingProfilesRetrieve } from "@/api/admin-payments-billing-profiles/admin-payments-billing-profiles";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import type { AdminAssociatedContactDetail } from "@/api/model";

export const useAssociatedContactDetail = () => {
  const { currentOrganizationId } = useCurrentOrganization();

  const contactDetail = ref<AdminAssociatedContactDetail | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const fetchContactDetail = async (contactId: number): Promise<void> => {
    if (!currentOrganizationId.value) {
      error.value = "No organization ID found";
      console.error("No organization ID found");
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const acResult = await adminV1OrganizationsAssociatedContactsRetrieve(
        currentOrganizationId.value,
        contactId
      );

      const billingProfileId =
        typeof acResult.billing_profile === "object" &&
        acResult.billing_profile !== null
          ? (acResult.billing_profile as unknown as { id: string }).id
          : acResult.billing_profile;

      // The BillingProfile is the only source of billing data (the AC's own
      // billing fields are being dropped from the backend), so without it the
      // contact keeps its identity fields but has no payment methods.
      let finalDetail: AdminAssociatedContactDetail = {
        ...acResult,
        payment_methods: [],
        payabli_customer_url: null,
        payabli_customer_number: null,
      };

      if (billingProfileId) {
        try {
          const bpDetail =
            await adminV1OrganizationsPaymentsBillingProfilesRetrieve(
              currentOrganizationId.value,
              billingProfileId
            );
          finalDetail = {
            ...acResult,
            payment_methods: bpDetail.payment_methods,
            payabli_customer_url: bpDetail.payabli_customer_url,
            payabli_customer_number: bpDetail.payabli_customer_number,
          };
        } catch (bpError) {
          console.warn(
            "[useAssociatedContactDetail] Failed to fetch billing profile",
            bpError
          );
          captureException(bpError, {
            extra: {
              message: "Failed to fetch billing profile detail",
            },
          });
        }
      }

      contactDetail.value = finalDetail;
    } catch (e: any) {
      error.value = e.message || "Failed to fetch contact detail";
      console.error("Failed to fetch contact detail:", e);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    contactDetail,
    isLoading,
    error,
    fetchContactDetail,
  };
};
